import * as React from "react";
import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ArrowUp, Mail, MessageSquare } from "lucide-react";
import { ReactComponent as MediusLogo } from "../../medius-logo.svg";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

const STYLES = `
.cinematic-footer-wrapper {
  font-family: 'Plus Jakarta Sans', sans-serif;
  -webkit-font-smoothing: antialiased;
}

@keyframes footer-breathe {
  0%   { transform: translate(-50%, -50%) scale(1);    opacity: 0.5; }
  100% { transform: translate(-50%, -50%) scale(1.15); opacity: 0.9; }
}

.animate-footer-breathe {
  animation: footer-breathe 8s ease-in-out infinite alternate;
}

.footer-bg-grid {
  background-size: 60px 60px;
  background-image:
    linear-gradient(to right, rgba(255,255,255,0.03) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(255,255,255,0.03) 1px, transparent 1px);
  mask-image: linear-gradient(to bottom, transparent, black 30%, black 70%, transparent);
  -webkit-mask-image: linear-gradient(to bottom, transparent, black 30%, black 70%, transparent);
}

.footer-aurora {
  background: radial-gradient(
    circle at 50% 40%,
    rgba(218,32,40,0.30) 0%,
    rgba(218,32,40,0.08) 45%,
    transparent 70%
  );
}

.footer-giant-bg-text {
  font-size: 26vw;
  line-height: 0.75;
  font-weight: 900;
  letter-spacing: -0.05em;
  color: transparent;
  -webkit-text-stroke: 1px rgba(255,255,255,0.05);
  background: linear-gradient(180deg, rgba(255,255,255,0.08) 0%, transparent 60%);
  -webkit-background-clip: text;
  background-clip: text;
}

.footer-text-glow {
  background: linear-gradient(180deg, #ffffff 0%, rgba(255,255,255,0.4) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  filter: drop-shadow(0px 0px 20px rgba(255,255,255,0.12));
}
`;

const footerColumns = [
  {
    title: "Products",
    links: ["AP Automation", "Payment Automation", "Procurement", "Expense Management", "Sourcing"],
  },
  {
    title: "Company",
    links: ["About us", "Careers", "Press", "Blog", "Partners"],
  },
  {
    title: "Legal",
    links: ["Privacy policy", "Terms of service", "Security", "Cookie settings"],
  },
];

export function CinematicFooter() {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const giantTextRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window === "undefined" || !wrapperRef.current) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        giantTextRef.current,
        { y: "10vh", scale: 0.8, opacity: 0 },
        {
          y: "0vh", scale: 1, opacity: 1, ease: "power1.out",
          scrollTrigger: {
            trigger: wrapperRef.current,
            start: "top 80%", end: "bottom bottom", scrub: 1,
          },
        }
      );

      gsap.fromTo(
        [headingRef.current, contentRef.current],
        { y: 50, opacity: 0 },
        {
          y: 0, opacity: 1, stagger: 0.15, ease: "power3.out",
          scrollTrigger: {
            trigger: wrapperRef.current,
            start: "top 40%", end: "bottom bottom", scrub: 1,
          },
        }
      );
    }, wrapperRef);

    return () => ctx.revert();
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: STYLES }} />

      <div
        ref={wrapperRef}
        id="contact"
        className="relative h-screen w-full"
        style={{ clipPath: "polygon(0% 0, 100% 0%, 100% 100%, 0 100%)" }}
      >
        <footer className="fixed bottom-0 left-0 flex h-screen w-full flex-col justify-between overflow-hidden bg-[#110909] text-white cinematic-footer-wrapper">

          {/* Aurora glow */}
          <div className="footer-aurora absolute left-1/2 top-1/2 h-[60vh] w-[80vw] -translate-x-1/2 -translate-y-1/2 animate-footer-breathe rounded-[50%] blur-[80px] pointer-events-none z-0" />
          <div className="footer-bg-grid absolute inset-0 z-0 pointer-events-none" />

          {/* Giant ghost text */}
          <div
            ref={giantTextRef}
            className="footer-giant-bg-text absolute -bottom-[5vh] left-1/2 -translate-x-1/2 whitespace-nowrap z-0 pointer-events-none select-none"
          >
            MEDIUS
          </div>

          {/* CTA center */}
          <div className="relative z-10 flex flex-1 flex-col items-center justify-center px-6 w-full max-w-5xl mx-auto">
            <h2
              ref={headingRef}
              className="text-4xl md:text-6xl lg:text-7xl font-bold footer-text-glow tracking-tight leading-[1.08] mb-4 text-center"
            >
              Ready to automate<br />your AP?
            </h2>
            <p className="text-slate-400 text-base mb-8 text-center max-w-md leading-relaxed">
              Join 500+ finance teams running on Medius. Go live in weeks, not months.
            </p>

            <div ref={contentRef} className="flex flex-wrap justify-center gap-3">
              <a
                href="mailto:info@medius.com"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-[#DA2028] hover:bg-[#C41B22] text-white font-semibold text-sm shadow-lg shadow-red-900/30 transition-all duration-200 cursor-pointer"
              >
                <Mail className="w-4 h-4" />
                Book a demo
              </a>
              <a
                href="mailto:sales@medius.com"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl border border-white/15 hover:border-white/30 hover:bg-white/8 text-white/65 hover:text-white font-semibold text-sm transition-all duration-200 cursor-pointer"
              >
                <MessageSquare className="w-4 h-4" />
                Talk to sales
              </a>
            </div>
          </div>

          {/* Footer nav columns */}
          <div className="relative z-20 w-full px-6 md:px-12 pt-8 pb-6 border-t border-white/8 grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-12">
            {footerColumns.map((col) => (
              <div key={col.title}>
                <div className="text-[10px] font-semibold text-white/35 uppercase tracking-widest mb-3">
                  {col.title}
                </div>
                <ul className="flex flex-col gap-1.5">
                  {col.links.map((link) => (
                    <li key={link}>
                      <a
                        href="#"
                        className="text-sm text-white/40 hover:text-white/75 transition-colors duration-150 cursor-pointer"
                      >
                        {link}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Bottom bar */}
          <div className="relative z-20 w-full pb-7 pt-4 px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-4 border-t border-white/5">
            <div className="text-white/25 text-[10px] md:text-xs font-semibold tracking-widest uppercase order-2 md:order-1">
              © 2026 Medius AB. All rights reserved.
            </div>

            <a href="#top" className="order-1 md:order-2 cursor-pointer">
              <MediusLogo className="h-5 w-auto brightness-0 invert opacity-30 hover:opacity-60 transition-opacity duration-200" />
            </a>

            <button
              onClick={scrollToTop}
              className="w-11 h-11 rounded-full border border-white/15 hover:border-white/30 hover:bg-white/8 flex items-center justify-center text-white/30 hover:text-white/70 group order-3 transition-all duration-200 cursor-pointer"
              aria-label="Scroll to top"
            >
              <ArrowUp className="w-4 h-4 group-hover:-translate-y-1 transition-transform duration-300" />
            </button>
          </div>
        </footer>
      </div>
    </>
  );
}
